import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KEY_ACTIONS, type KeyAction } from '@il-colpo/shared/settings';
import { useSettingsStore } from '../../state/settingsStore.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { codeLabel, rebind, resetKeybindings } from '../../systems/input/keybindings.ts';
import { Button } from '../../ui/index.ts';
import styles from './Settings.module.css';

/** Keyboard remapping (spec §6). Touch and gamepad bindings are fixed. */
export function ControlsTab() {
  const { t } = useTranslation('settings');
  const bindings = useSettingsStore((s) => s.settings.controls.keybindings);
  const update = useSettingsStore((s) => s.update);
  const push = useToastStore((s) => s.push);
  const [listening, setListening] = useState<KeyAction | null>(null);

  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'Escape') {
        setListening(null);
        return;
      }
      const result = rebind(bindings, listening, e.code);
      if (!result.ok) {
        push(
          t('controls.conflict', {
            key: codeLabel(e.code),
            action: t(`controls.actions.${result.conflict}`),
          }),
          'danger',
        );
        return;
      }
      update((s) => {
        s.controls.keybindings = result.bindings;
      });
      setListening(null);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
    };
  }, [listening, bindings, update, push, t]);

  return (
    <div className={styles['stack']}>
      <p className={styles['note']}>{t('controls.hint')}</p>
      <ul className={styles['keyList']}>
        {KEY_ACTIONS.map((action) => (
          <li key={action} className={styles['keyRow']}>
            <span>{t(`controls.actions.${action}`)}</span>
            <Button
              variant="secondary"
              selected={listening === action}
              onClick={() => {
                setListening(listening === action ? null : action);
              }}
            >
              {listening === action ? t('controls.pressKey') : codeLabel(bindings[action])}
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="ghost"
        onClick={() => {
          update((s) => {
            s.controls.keybindings = resetKeybindings();
          });
          setListening(null);
        }}
      >
        {t('controls.resetAll')}
      </Button>
    </div>
  );
}
